/**
 * EntitySelector
 *
 * @typedef {Object} EntitySelector
 * @property {boolean} open - Determines if the modal is open.
 * @property {() => void} onClose - Function to close the modal.
 * @property {string} title - Title of the modal.
 * @property {any[]} listEntities - List of entities to display.
 * @property {(search: string, data: any) => boolean} filterCriteria - Function to filter the entities based on search criteria.
 * @property {(selected: any) => void} onSelect - Function to handle the selection of an entity.
 * @property {string} keyField - The key field to uniquely identify each entity.
 */

import { BaseSyntheticEvent, FC, memo, useMemo, useState } from "react";
import React from "react";
import { Modal } from "components/Modal";
import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DomainColorSquares } from "components/Requirements";
import { sortByDisplayName } from "utils/Array";

type EntitySelectorProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  listEntities: any[];
  filterCriteria: (search: string, data: any) => boolean;
  onSelect: (selected: any) => void;
  keyField: string;
  displayField?: string;
  displayImageField?: string | null;
  displayFieldSecondary?: string | null;
  enableNull?: boolean;
  nullDisplay?: string;
  renderItemPrefix?: (data: any) => React.ReactNode;
  closeOnSelect?: boolean;
  selectedKey?: string | number | null;
  lockOpen?: boolean;
};

export const EntitySelectorComponent: FC<EntitySelectorProps> = ({
  open,
  onClose,
  title,
  listEntities,
  filterCriteria,
  onSelect,
  keyField,
  displayField = "name",
  displayImageField = null,
  displayFieldSecondary = null,
  enableNull = false,
  nullDisplay = "Any",
  renderItemPrefix,
  closeOnSelect = true,
  selectedKey = null,
  lockOpen = false,
}) => {
  const [search, setSearch] = useState<string>("");
  const filteredListEntities = useMemo(() => {
    const filtered =
      search === ""
        ? listEntities
        : listEntities.filter((data) => filterCriteria(search, data));
    return sortByDisplayName(filtered, displayField);
  }, [search, listEntities, filterCriteria, displayField]);

  const handleOnSelect = (data: any) => {
    onSelect(data);
    if (closeOnSelect) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="small"
      hideCloseButton={lockOpen}
      disableAutoFocus={lockOpen}
    >
      <Box mt={2}>
        <Box>
          <TextField
            placeholder="Search"
            variant="outlined"
            fullWidth
            value={search}
            onChange={(e: BaseSyntheticEvent) => setSearch(e.target.value)}
          />
        </Box>
        <Box
          sx={{
            height: 300,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {filteredListEntities?.length > 0 && (
            <List>
              {filteredListEntities?.map((data, i) => (
                <ListItem
                  key={data[keyField]}
                  disablePadding
                  sx={{
                    borderBottom:
                      i === filteredListEntities.length - 1 && !enableNull
                        ? "none"
                        : "1px solid rgba(0,0,0,0.2)",
                  }}
                >
                  <ListItemButton
                    selected={
                      selectedKey != null &&
                      String(data[keyField]) === String(selectedKey)
                    }
                    onClick={() => handleOnSelect(data)}
                  >
                    {renderItemPrefix ? (
                      <ListItemAvatar>{renderItemPrefix(data)}</ListItemAvatar>
                    ) : displayImageField && data[displayImageField] ? (
                      <ListItemAvatar>
                        <Avatar
                          src={data[displayImageField]}
                          alt={data[displayField]}
                          sx={{ width: 40, height: 40 }}
                        />
                      </ListItemAvatar>
                    ) : null}
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="body1" component="span">
                          {data[displayField]}
                        </Typography>
                      }
                      secondary={
                        <Stack spacing={0.5} alignItems="flex-start">
                          {displayFieldSecondary ? (
                            <Typography variant="body2" color="text.secondary">
                              {data[displayFieldSecondary]}
                            </Typography>
                          ) : null}
                          <DomainColorSquares requirement={data} />
                        </Stack>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
          {enableNull && (
            <ListItem key={"null_element"} disablePadding>
              <ListItemButton onClick={() => handleOnSelect(null)}>
                <ListItemText primary={nullDisplay} />
              </ListItemButton>
            </ListItem>
          )}
          {filteredListEntities?.length === 0 && (
            <Typography variant="body2" align="center" sx={{ margin: 2 }}>
              Doesn't exists
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export const EntitySelector = memo(EntitySelectorComponent);
